'use client'

/**
 * Pricing section, extracted verbatim from LandingPage.tsx (no visual/behavioral change).
 * Renders the `PLANS` structural data (price/checkout facts) and takes the two localized
 * chrome strings (`popularLabel`, `checkoutStartingLabel`) + checkout handler/state as props,
 * so localization and Stripe checkout behave exactly as they did inline. Design-system rule:
 * MC tokens + SERIF only, `.lp` class names preserved.
 */

import type { CSSProperties } from 'react'
import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'

export type PricingPlan = {
  id: string
  name: string
  blurb: string
  price: string
  per: string
  /** e.g. "billed annually ($240/yr)" — shown under the monthly-equivalent headline price so the
   *  actual (single, annual) charge is never implied to be a recurring monthly one. */
  subNote?: string
  cta: string
  features: readonly string[]
  featured: boolean
  checkoutPlan?: 'living' | 'plus' | 'wealth' | 'advisor'
}

const H2: CSSProperties = { color: MC.ink, fontFamily: SERIF, fontWeight: 400, fontSize: 46, lineHeight: 1.08, letterSpacing: '-0.02em', textWrap: 'balance' }

export default function PricingGrid({
  plans,
  popularLabel,
  checkoutStartingLabel,
  titleBefore,
  titleEm,
  titleAfter,
  body,
  startCheckout,
  checkoutBusy,
  checkoutError,
  renewalTitle,
  renewalBody,
  posture,
}: {
  plans: readonly PricingPlan[]
  popularLabel: string
  checkoutStartingLabel: string
  titleBefore: string
  titleEm: string
  titleAfter: string
  body: string
  startCheckout: (plan: 'living' | 'plus' | 'wealth' | 'advisor') => void
  checkoutBusy: string | null
  checkoutError: string | null
  renewalTitle?: string
  renewalBody?: string
  posture?: string
}) {
  return (
    <section id="pricing" className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '112px 32px' }}>
      <h2 className="mc-h2" style={{ ...H2, margin: '0 0 12px' }}>
        {titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{titleEm}</em>{titleAfter}
      </h2>
      <p style={{ fontSize: 18, color: MC.muted, margin: '0 0 56px' }}>{body}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(244px, 1fr))', gap: 20 }}>
        {plans.map((plan) => {
          const isContactTier = plan.id === 'advisor' || plan.id === 'crossBorder'
          const href = isContactTier ? 'mailto:hello@mycio.ai' : '/sign-in#signup'
          return (
            <div key={plan.id} className="mc-pricecard" style={{ position: 'relative', background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 14, padding: '30px 26px', display: 'flex', flexDirection: 'column' }}>
              {plan.featured && (
                <span style={{ position: 'absolute', top: -11, left: 26, background: MC.forest, color: MC.bg, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '5px 12px', borderRadius: 999 }}>{popularLabel}</span>
              )}
              <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 24, margin: '0 0 4px', color: MC.ink }}>{plan.name}</h3>
              {/* Reserve a fixed 2-line height for the blurb so a card whose blurb wraps (e.g. the $499
                  "MyCIO builds the complete plan") doesn't push its price row below the others' — the price
                  rows must line up across cards at every width. */}
              <p style={{ fontSize: 14.5, lineHeight: 1.4, color: MC.faint, margin: '0 0 20px', minHeight: 42 }}>{plan.blurb}</p>
              <div style={{ marginBottom: 24, minHeight: 40 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontFamily: SERIF, fontSize: 36, fontWeight: 500, color: MC.ink }}>{plan.price}</span>
                  <span style={{ fontSize: 15, color: MC.faint }}>{plan.per}</span>
                </div>
                {plan.subNote && (
                  <p style={{ fontSize: 13, color: MC.faint, margin: '4px 0 0' }}>{plan.subNote}</p>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                {plan.features.map((feat) => (
                  <div key={feat} style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                    <span style={{ flex: 'none', width: 6, height: 6, borderRadius: 999, background: MC.forest, marginTop: 8 }} />
                    <span style={{ fontSize: 15, lineHeight: 1.4, color: MC.inkSoft }}>{feat}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'auto' }}>
                {plan.checkoutPlan ? (
                  // Self-serve checkout. Plus/Wealth are one subscription contract: the build charge is on
                  // the initial invoice and the recurring price begins after the included first year.
                  <button
                    type="button"
                    onClick={() => startCheckout(plan.checkoutPlan!)}
                    disabled={!!checkoutBusy}
                    className={plan.featured ? 'mc-cta-primary' : 'mc-cta-secondary'}
                    style={
                      plan.featured
                        ? { width: '100%', textAlign: 'center', fontSize: 15, fontWeight: 600, color: MC.bg, background: MC.forest, padding: 13, borderRadius: 999, border: 'none', cursor: checkoutBusy ? 'wait' : 'pointer' }
                        : { width: '100%', textAlign: 'center', fontSize: 15, fontWeight: 600, color: MC.ink, background: 'transparent', border: '1px solid #C9C2B2', padding: 12, borderRadius: 999, cursor: checkoutBusy ? 'wait' : 'pointer' }
                    }
                  >
                    {checkoutBusy === plan.checkoutPlan ? checkoutStartingLabel : plan.cta}
                  </button>
                ) : plan.featured ? (
                  <Link href={href} className="mc-cta-primary" style={{ display: 'block', textAlign: 'center', fontSize: 15, fontWeight: 600, color: MC.bg, background: MC.forest, padding: 13, borderRadius: 999, textDecoration: 'none' }}>{plan.cta}</Link>
                ) : (
                  <Link href={href} className="mc-cta-secondary" style={{ display: 'block', textAlign: 'center', fontSize: 15, fontWeight: 600, color: MC.ink, background: 'transparent', border: '1px solid #C9C2B2', padding: 12, borderRadius: 999, textDecoration: 'none' }}>{plan.cta}</Link>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {checkoutError && (
        <p role="alert" style={{ marginTop: 18, textAlign: 'center', fontSize: 14.5, color: '#8A3A1E' }}>
          {checkoutError}
        </p>
      )}
      {(renewalTitle || renewalBody) && (
        <div style={{ marginTop: 24, padding: '20px 22px', border: `1px solid ${MC.border}`, borderRadius: 14, background: '#F4F0E7' }}>
          {renewalTitle && (
            <h3 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: MC.ink, margin: '0 0 8px' }}>{renewalTitle}</h3>
          )}
          {renewalBody && (
            <p style={{ fontSize: 15.5, lineHeight: 1.55, color: MC.inkSoft, margin: 0 }}>{renewalBody}</p>
          )}
        </div>
      )}
      {posture && (
        <p style={{ margin: '18px auto 0', maxWidth: 860, textAlign: 'center', fontSize: 14.5, lineHeight: 1.55, color: MC.faint }}>
          {posture}
        </p>
      )}
    </section>
  )
}
